import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError, listMaterials, searchMaterials, uploadMaterial } from "@/lib/api";
import {
  MATERIAL_KINDS,
  materialHitText,
  materialRowStatus,
  materialSearchHits,
  type MaterialKind,
  type MaterialRow,
} from "@/lib/materials";
import { scopeLoadKey, useKeyedLoad } from "@/lib/scope-loading";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";

const EMPTY_COPY = "No materials yet — upload a PDF/MD…";

function formatBytes(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function ProjectMaterialsPanel({ compact = false }: { compact?: boolean }) {
  const { orgSlug, projectSlug } = useParams();
  const fileInput = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<MaterialRow[]>([]);
  const [kind, setKind] = useState<MaterialKind>("datasheet");
  const [mpn, setMpn] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState<string | null>(null);
  const [hits, setHits] = useState<unknown[] | null>(null);
  const routeKey = scopeLoadKey(orgSlug, projectSlug);
  const { loading, start, finish } = useKeyedLoad(routeKey);

  const load = useCallback(async () => {
    const isCurrent = start();
    if (!orgSlug || !projectSlug) {
      finish(isCurrent);
      return;
    }
    try {
      const body = await listMaterials(orgSlug, projectSlug);
      if (!isCurrent()) return;
      setItems(Array.isArray(body.items) ? body.items : []);
      setErr(null);
    } catch (e) {
      if (!isCurrent()) return;
      setItems([]);
      setErr(e instanceof ApiError ? e.message : e instanceof Error ? e.message : "materials load failed");
    } finally {
      finish(isCurrent);
    }
  }, [orgSlug, projectSlug, start, finish]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onUpload(file: File) {
    if (!orgSlug || !projectSlug) return;
    setBusy(true);
    setErr(null);
    try {
      const result = await uploadMaterial(orgSlug, projectSlug, file, {
        kind,
        mpn: mpn.trim() || undefined,
      });
      if (result.needs_ocr) {
        setErr("Stored on R2 — needs OCR; not indexed.");
      } else if (!result.ai_search_item_id) {
        setErr(result.message || "Stored — not indexed (no AI Search item id).");
      }
      setMpn("");
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : e instanceof Error ? e.message : "upload failed");
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function onSearch(e: FormEvent) {
    e.preventDefault();
    if (!orgSlug || !projectSlug) return;
    const q = query.trim();
    if (!q) {
      setSearchErr("query required");
      setHits(null);
      return;
    }
    setSearching(true);
    setSearchErr(null);
    try {
      const body = await searchMaterials(orgSlug, projectSlug, q);
      setHits(materialSearchHits(body));
    } catch (e) {
      setHits(null);
      setSearchErr(e instanceof ApiError ? e.message : e instanceof Error ? e.message : "search failed");
    } finally {
      setSearching(false);
    }
  }

  if (!orgSlug || !projectSlug) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="project-materials">
        Open a project to manage materials.
      </p>
    );
  }

  return (
    <div data-testid="project-materials">
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <label className="grid gap-1 text-[11px] tracking-wide text-muted-foreground uppercase">
          Kind
          <select
            data-testid="materials-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as MaterialKind)}
            className="h-8 rounded-md border border-input bg-transparent px-2 text-xs font-normal tracking-normal text-foreground normal-case"
          >
            {MATERIAL_KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
        {kind === "datasheet" && (
          <label className="grid gap-1 text-[11px] tracking-wide text-muted-foreground uppercase">
            MPN
            <Input
              data-testid="materials-mpn"
              value={mpn}
              onChange={(e) => setMpn(e.target.value)}
              placeholder="optional"
              className="h-8 w-36 text-xs"
            />
          </label>
        )}
        <label className="inline-flex">
          <input
            data-testid="materials-upload-input"
            data-mission-danger=""
            ref={fileInput}
            type="file"
            accept=".pdf,.md,.txt,.csv,.json,application/pdf,text/markdown,text/plain,text/csv,application/json"
            className="hidden"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onUpload(file);
              e.target.value = "";
            }}
          />
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            data-testid="materials-upload"
            data-mission-id="action:upload-material"
            data-mission-danger=""
            onClick={() => fileInput.current?.click()}
          >
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </label>
      </div>
      {err && (
        <p className="mb-3 text-sm text-destructive" role="alert" data-testid="materials-error">
          {err}
        </p>
      )}
      {loading ? (
        <div className="space-y-2" role="status" data-testid="materials-loading">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-3/4" />
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-md border border-dashed border-border bg-muted/20 px-4 py-6 text-center text-sm text-muted-foreground"
          data-testid="materials-empty"
        >
          {EMPTY_COPY}
        </div>
      ) : (
        <ul
          className={compact ? "max-h-48 space-y-1 overflow-auto" : "space-y-1"}
          data-testid="materials-list"
        >
          {items.map((row) => {
            const st = materialRowStatus(row);
            return (
              <li
                key={row.id}
                className="flex flex-wrap items-center gap-2 rounded-md border border-border px-2 py-1.5 text-xs"
                data-testid="materials-item"
              >
                <span className="min-w-0 flex-1 truncate font-mono text-foreground">{row.filename}</span>
                <Badge variant="outline">{row.kind}</Badge>
                {row.mpn ? <span className="text-muted-foreground">{row.mpn}</span> : null}
                {formatBytes(row.byte_size) ? (
                  <span className="text-muted-foreground">{formatBytes(row.byte_size)}</span>
                ) : null}
                <Badge
                  variant={st.indexed ? "outline" : "secondary"}
                  data-testid="materials-item-status"
                  title={
                    st.indexed
                      ? `ai_search_item_id ${row.ai_search_item_id}`
                      : `parser=${row.parser ?? "null"} ai_search_item_id=${row.ai_search_item_id ?? "null"}`
                  }
                >
                  {st.label}
                </Badge>
                {row.parser ? (
                  <span className="font-mono text-[10px] text-muted-foreground">{row.parser}</span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <form className="mt-4 flex flex-wrap items-center gap-2" onSubmit={onSearch} data-testid="materials-search">
        <Input
          data-testid="materials-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search project knowledge"
          className="h-8 max-w-sm text-xs"
        />
        <Button size="sm" variant="outline" type="submit" disabled={searching}>
          {searching ? "Searching…" : "Search"}
        </Button>
      </form>
      {searchErr && (
        <p className="mt-2 text-sm text-destructive" role="alert" data-testid="materials-search-error">
          {searchErr}
        </p>
      )}
      {hits && (
        <div className="mt-2 space-y-1" data-testid="materials-search-results">
          {hits.length === 0 ? (
            <p className="text-sm text-muted-foreground">No matches.</p>
          ) : (
            hits.map((hit, i) => (
              <p key={i} className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground">
                {materialHitText(hit)}
              </p>
            ))
          )}
        </div>
      )}
    </div>
  );
}
